import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { useAuth } from "@/context/AuthContext";
import { PageTitle, Fab, StickyTotal, RowActions, EmptyState, SideIcon } from "@/components/shared";
import DataDialog from "@/components/DataDialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link2, MessageCircle, Send, CheckCheck, Settings2 } from "lucide-react";
import { toast } from "sonner";
import WATemplateDialog, { DEFAULT_WA_TEMPLATE, renderTemplate } from "@/components/WATemplateDialog";
import { formatDate } from "@/lib/format";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";

const KET = ["Keluarga", "Teman", "Rekan Kerja", "Tetangga", "Rekan Orang Tua", "Sahabat", "VIP", "Lain-lain"];

function SideBlock({ side, items, onEdit, onDelete, onLink, onWA, selected, onToggle }) {
  const rows = items.filter((i) => i.tamu_dari === side);
  return (
    <div className="mb-8">
      <h2 className="mb-3 flex items-center gap-2 font-heading text-2xl"><SideIcon side={side} /> Tamu {side} <span className="text-sm text-muted-foreground">({rows.length})</span></h2>
      {rows.length === 0 ? <EmptyState text="Belum ada tamu." /> : KET.map((k) => {
        const g = rows.filter((r) => r.keterangan === k);
        if (g.length === 0) return null;
        return (
          <div key={k} className="mb-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{k} ({g.length})</p>
            <div className="rounded-xl border border-border">
              <Table>
                <TableHeader><TableRow><TableHead className="w-10"></TableHead><TableHead>Nama Tamu</TableHead><TableHead>Status WA</TableHead><TableHead className="text-right">Link & Aksi</TableHead></TableRow></TableHeader>
                <TableBody>
                  {g.map((i) => (
                    <TableRow key={i.id} data-testid={`row-${i.id}`}>
                      <TableCell><Checkbox data-testid={`select-${i.id}`} checked={selected.has(i.id)} onCheckedChange={() => onToggle(i.id)} /></TableCell>
                      <TableCell className="font-medium">{i.nama}</TableCell>
                      <TableCell>{i.wa_sent ? <Badge className="bg-green-600 gap-1"><CheckCheck className="h-3 w-3" />Terkirim</Badge> : <Badge variant="outline">Belum</Badge>}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button data-testid={`wa-${i.id}`} size="icon" variant="ghost" className="h-8 w-8 text-green-600 hover:text-green-700" title="Bagikan via WhatsApp" onClick={() => onWA(i)}>
                            <MessageCircle className="h-4 w-4" />
                          </Button>
                          <Button data-testid={`link-${i.id}`} size="icon" variant="ghost" className="h-8 w-8" title="Salin link undangan tamu ini" onClick={() => onLink(i)}>
                            <Link2 className="h-4 w-4" />
                          </Button>
                          <RowActions id={i.id} onEdit={() => onEdit(i)} onDelete={() => onDelete(i.id)} />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Tamu() {
  const { data, create, update, remove, activePasangan, pasanganInfo } = useData();
  const { user } = useAuth();
  const items = data.tamu || [];
  const templates = data.wa_templates || [];
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [tplDialogOpen, setTplDialogOpen] = useState(false);
  const [tplId, setTplId] = useState("__default__");
  const [settings, setSettings] = useState({});
  const settingsPid = user?.role === "sysadmin" || user?.role === "Admin" ? activePasangan : user?.pasangan_id;
  const pid = settingsPid;
  const { getQuota, planLabel } = useFeatureAccess();
  const tamuQuota = getQuota("max_tamu"); // null = unlimited
  const waQuota = getQuota("max_wa_templates");

  useEffect(() => {
    if (!pid) return;
    const params = (user?.role === "sysadmin" || user?.role === "Admin") ? { pasangan_id: pid } : {};
    api.get("/settings", { params }).then((r) => setSettings(r.data || {})).catch(() => {});
  }, [pid, user?.role]);

  const activeTpl = templates.find((t) => t.id === tplId);
  const activeTplIsi = activeTpl ? activeTpl.isi : DEFAULT_WA_TEMPLATE;

  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const eventForVars = (settings.events && settings.events[0]) || {};
  const tglRaw = eventForVars.tanggal || settings.wedding_date;
  const lokasi = eventForVars.tempat || settings.venue_name || "";

  const buildLink = (guest) => `${window.location.origin}/u/${pid}?to=${encodeURIComponent(guest.nama)}`;

  const buildCtx = (guest) => {
    const link = buildLink(guest);
    return {
      nama: guest.nama,
      pasangan: pasanganInfo?.nama || pid || "",
      tanggal: tglRaw ? formatDate(tglRaw) : "",
      lokasi,
      link_undangan: link,
    };
  };

  const buildMessage = (guest) => renderTemplate(activeTplIsi, buildCtx(guest));

  const buildWaUrl = (guest) => {
    const msg = buildMessage(guest);
    let phone = String(guest.telepon || "").replace(/\D/g, "");
    if (phone.startsWith("0")) phone = "62" + phone.slice(1);
    return phone ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`;
  };

  const markSent = (guest) => update("tamu", guest.id, { wa_sent: true, wa_sent_at: new Date().toISOString() });

  const copyLink = async (guest) => {
    if (!pid) { toast.error("Pasangan belum dipilih"); return; }
    const link = buildLink(guest);
    try { await navigator.clipboard.writeText(link); toast.success(`Link undangan untuk ${guest.nama} disalin!`); }
    catch { toast.info(link); }
  };

  const fields = [
    { name: "tamu_dari", label: "Tamu Dari", type: "select", options: ["Pria", "Wanita"], required: true },
    { name: "nama", label: "Nama Tamu", type: "text", required: true },
    { name: "keterangan", label: "Keterangan", type: "select", options: KET, required: true },
    { name: "telepon", label: "No. WhatsApp (opsional, mis. 0812...)", type: "text" },
  ];

  const shareWA = (guest) => {
    if (!pid) { toast.error("Pasangan belum dipilih"); return; }
    window.open(buildWaUrl(guest), "_blank");
    markSent(guest);
  };

  const createTpl = (payload) => {
    if (waQuota !== null && templates.length >= waQuota) {
      toast.error(`Batas template WA (${waQuota}) untuk ${planLabel} sudah tercapai. Upgrade paket untuk menambah.`);
      return Promise.reject(new Error("quota_reached"));
    }
    return create("wa_templates", payload);
  };
  const updateTpl = (id, payload) => update("wa_templates", id, payload);
  const removeTpl = (id) => { remove("wa_templates", id); if (tplId === id) setTplId("__default__"); };

  const selectedGuests = items.filter((i) => selected.has(i.id));
  const openBulk = () => {
    if (selectedGuests.length === 0) { toast.error("Pilih minimal satu tamu"); return; }
    setBulkOpen(true);
  };

  const onSave = async (payload) => {
    if (editing) { await update("tamu", editing.id, payload); toast.success("Tamu diperbarui"); }
    else {
      if (tamuQuota !== null && items.length >= tamuQuota) {
        toast.error(`Batas tamu (${tamuQuota}) untuk ${planLabel} sudah tercapai. Upgrade paket untuk menambah lebih banyak.`);
        throw new Error("quota_reached");
      }
      await create("tamu", payload); toast.success("Tamu ditambahkan");
    }
  };

  const totalPria = items.filter((i) => i.tamu_dari === "Pria").length;
  const totalWanita = items.filter((i) => i.tamu_dari === "Wanita").length;

  return (
    <div>
      <PageTitle title="List Tamu" subtitle="Dikelompokkan per pihak & kategori" />
      <StickyTotal testId="tamu-total">
        <div className="flex items-center justify-between">
          <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Total Tamu</p>
            <p className="font-heading text-3xl font-semibold" data-testid="tamu-total-value">{items.length}</p></div>
          <div className="flex gap-6 text-sm">
            <div><SideIcon side="Pria" /> Pria: <b>{totalPria}</b></div>
            <div><SideIcon side="Wanita" /> Wanita: <b>{totalWanita}</b></div>
          </div>
        </div>
      </StickyTotal>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-muted-foreground">Template WA:</span>
          <Select value={tplId} onValueChange={setTplId}>
            <SelectTrigger data-testid="wa-template-select" className="h-9 w-56 rounded-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__default__">Template Default</SelectItem>
              {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.nama}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="wa-template-settings" onClick={() => setTplDialogOpen(true)} size="icon" variant="outline" className="h-9 w-9 rounded-full" title="Kelola template WhatsApp">
            <Settings2 className="h-4 w-4" />
          </Button>
        </div>
        <Button data-testid="bulk-wa-btn" onClick={openBulk} disabled={selected.size === 0} className="gap-2 rounded-full">
          <Send className="h-4 w-4" /> Kirim WhatsApp Massal ({selected.size})
        </Button>
        {selected.size > 0 && <Button variant="ghost" size="sm" data-testid="clear-selection" onClick={() => setSelected(new Set())}>Bersihkan pilihan</Button>}
      </div>
      <SideBlock side="Pria" items={items} onEdit={(i) => { setEditing(i); setOpen(true); }} onDelete={(id) => remove("tamu", id)} onLink={copyLink} onWA={shareWA} selected={selected} onToggle={toggle} />
      <SideBlock side="Wanita" items={items} onEdit={(i) => { setEditing(i); setOpen(true); }} onDelete={(id) => remove("tamu", id)} onLink={copyLink} onWA={shareWA} selected={selected} onToggle={toggle} />
      <Fab testId="fab-tamu" onClick={() => { setEditing(null); setOpen(true); }} />
      <DataDialog open={open} onOpenChange={setOpen} title={editing ? "Edit Tamu" : "Tambah Tamu"} fields={fields} initial={editing} onSave={onSave} />

      <WATemplateDialog open={tplDialogOpen} onOpenChange={setTplDialogOpen} templates={templates} onCreate={createTpl} onUpdate={updateTpl} onRemove={removeTpl} />

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md" data-testid="bulk-wa-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Antrian WhatsApp ({selectedGuests.length})</DialogTitle>
            <DialogDescription>Klik "Kirim" pada tiap tamu untuk membuka WhatsApp & menandai terkirim. WhatsApp membuka satu per satu agar tidak diblokir browser.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {selectedGuests.map((gt) => (
              <div key={gt.id} data-testid={`bulk-row-${gt.id}`} className="flex items-center justify-between gap-2 rounded-xl border border-border p-3">
                <div>
                  <p className="text-sm font-medium">{gt.nama} {gt.wa_sent && <CheckCheck className="ml-1 inline h-3.5 w-3.5 text-green-600" />}</p>
                  <p className="text-xs text-muted-foreground">{gt.telepon || "tanpa nomor — pilih kontak manual"}</p>
                </div>
                <Button size="sm" data-testid={`bulk-send-${gt.id}`} className="gap-1 rounded-full bg-green-600 hover:bg-green-700"
                  onClick={() => { window.open(buildWaUrl(gt), "_blank"); markSent(gt); }}>
                  <MessageCircle className="h-3.5 w-3.5" /> Kirim
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
